import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../components/Sidebar";
import api from "../api/axios";

/* =========================================================
   Export utilities — CSV / Excel / PDF (no external libs)
   ========================================================= */
function exportCSV(filename, headers, rows) {
  const esc = (v) => { const s = String(v ?? ""); return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function exportExcel(filename, title, headers, rows) {
  const th = (c) => `<th style="background:#1E3A5F;color:#fff;padding:6px 10px;border:1px solid #ccc">${c ?? ""}</th>`;
  const td = (c) => `<td style="padding:5px 10px;border:1px solid #ddd">${c ?? ""}</td>`;
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body>
    <h3>${title}</h3>
    <table><thead><tr>${headers.map(th).join("")}</tr></thead>
    <tbody>${rows.map((r) => `<tr>${r.map(td).join("")}</tr>`).join("")}</tbody></table>
  </body></html>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function exportPDF(title, headers, rows) {
  const th = (c) => `<th style="background:#1E3A5F;color:#fff;padding:5px 8px;border:1px solid #ccc;font-size:10px">${c ?? ""}</th>`;
  const td = (c, i) => `<td style="padding:4px 8px;border:1px solid #ddd;font-size:10px;background:${i%2===0?'#fff':'#F8FAFC'}">${c ?? ""}</td>`;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>body{font-family:Arial,sans-serif;margin:16px}h2{font-size:13px;margin:0 0 10px}table{border-collapse:collapse;width:100%}
    @media print{@page{size:landscape A4;margin:8mm}}</style></head><body>
    <h2>${title}</h2>
    <table><thead><tr>${headers.map(th).join("")}</tr></thead>
    <tbody>${rows.map((r, i) => `<tr>${r.map((c) => td(c, i)).join("")}</tr>`).join("")}</tbody></table>
    <script>window.onload=function(){window.print()}<\/script></body></html>`;
  const win = window.open("", "_blank");
  if (win) { win.document.write(html); win.document.close(); }
}

/* =========================================================
   Mobile breakpoint hook
   ========================================================= */
function formatDob(dob) {
  if (!dob) return "—";
  const parts = String(dob).slice(0, 10).split("-");
  if (parts.length !== 3) return dob;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function useIsMobile(bp = 768) {
  const [m, setM] = useState(() => window.innerWidth < bp);
  useEffect(() => {
    const fn = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]);
  return m;
}

/* =========================================================
   Inject responsive styles (once)
   ========================================================= */
const TD_STYLES = `
  /* ---- bottom nav ---- */
  .td-bottom-nav {
    display: none;
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 200;
    background: #fff; border-top: 1px solid #E2E8F0;
    box-shadow: 0 -4px 20px rgba(0,0,0,.08);
    padding: 0; height: 62px;
  }
  .td-bottom-nav button {
    flex: 1; background: none; border: none; cursor: pointer;
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; gap: 3px; padding: 6px 4px;
    font-size: 11px; font-weight: 600; color: #94A3B8;
    transition: color .15s;
    -webkit-tap-highlight-color: transparent;
  }
  .td-bottom-nav button.active { color: #7C3AED; }
  .td-bottom-nav button span.nav-icon { font-size: 20px; line-height: 1; }

  /* ---- layout ---- */
  @media (max-width: 767px) {
    .td-bottom-nav { display: flex !important; }
    .td-sidebar-wrap { display: none !important; }
    .td-main { padding: 16px 12px 80px !important; }
    .td-hide-mobile { display: none !important; }
    .td-marks-desktop { display: none !important; }
    .td-marks-mobile  { display: flex !important; }
    .td-att-desktop   { display: none !important; }
    .td-att-mobile    { display: flex !important; }
    .td-stat-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; }
    .td-assign-grid { grid-template-columns: 1fr !important; }
    .td-profile-grid { grid-template-columns: 1fr !important; }
    .td-form-grid { grid-template-columns: 1fr !important; }
    .td-card { border-radius: 14px !important; padding: 16px !important; }
    .td-pw-wrap { max-width: 100% !important; }
    .td-csv-header { flex-direction: column !important; align-items: flex-start !important; }
    .td-sheet-actions { flex-wrap: wrap; gap: 6px; }
    .td-card-header { flex-direction: column !important; align-items: flex-start !important; }
    .td-att-header { flex-direction: column !important; align-items: flex-start !important; }
    .td-remarks-header { flex-direction: column !important; align-items: flex-start !important; }
    .td-remarks-row { flex-direction: column !important; }
    .td-remarks-save-col { flex-direction: row !important; align-items: center !important; gap: 8px !important; padding-top: 0 !important; }
  }
  @media (min-width: 768px) {
    .td-marks-desktop { display: block !important; }
    .td-marks-mobile  { display: none !important; }
    .td-att-desktop   { display: block !important; }
    .td-att-mobile    { display: none !important; }
  }

  /* ---- cards ---- */
  .td-card {
    background: #fff; border: 1px solid #E8EDF5; border-radius: 18px;
    padding: 22px 24px; margin-bottom: 18px;
    box-shadow: 0 1px 4px rgba(0,0,0,.04), 0 4px 16px rgba(0,0,0,.04);
  }
  .td-section-title {
    font-size: 18px; font-weight: 800; color: #1E293B; margin: 0 0 4px;
  }
  .td-section-sub {
    font-size: 13px; color: #64748B; margin: 0 0 20px;
  }

  /* ---- stat card ---- */
  .td-stat { cursor: pointer; transition: transform .15s, box-shadow .15s; }
  .td-stat:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,.1); }
  .td-stat-no-hover { cursor: default; }

  /* ---- student row cards (mobile marks) ---- */
  .td-student-card {
    background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 12px;
    padding: 14px 16px; display: flex; flex-direction: column; gap: 10px;
  }
  .td-student-card.pending { border-left: 3px solid #F59E0B; }
  .td-student-card.entered { border-left: 3px solid #10B981; }

  /* ---- badge pill ---- */
  .td-pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 700;
  }
  .td-pill-purple { background: #EDE9FE; color: #6D28D9; border: 1px solid #C4B5FD; }
  .td-pill-green  { background: #D1FAE5; color: #065F46; border: 1px solid #6EE7B7; }
  .td-pill-amber  { background: #FEF3C7; color: #92400E; border: 1px solid #FCD34D; }
  .td-pill-red    { background: #FEE2E2; color: #991B1B; border: 1px solid #FCA5A5; }
  .td-pill-blue   { background: #DBEAFE; color: #1D4ED8; border: 1px solid #93C5FD; }
  .td-pill-gray   { background: #F1F5F9; color: #475569; border: 1px solid #CBD5E1; }

  /* ---- section divider ---- */
  .td-divider { border: none; border-top: 1px solid #F1F5F9; margin: 14px 0; }

  /* ---- input touch-friendly ---- */
  @media (max-width: 767px) {
    .td-card input, .td-card select, .td-card textarea {
      font-size: 16px !important; /* prevents iOS zoom */
    }
  }

  /* ---- pct bar ---- */
  .td-pct-bar-track {
    height: 6px; background: #E2E8F0; border-radius: 4px; overflow: hidden; margin-top: 4px;
  }
  .td-pct-bar-fill { height: 100%; border-radius: 4px; transition: width .3s; }
`;

function useInjectStyles() {
  useEffect(() => {
    const id = "td-styles";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = TD_STYLES;
    document.head.appendChild(el);
    return () => { const s = document.getElementById(id); if (s) s.remove(); };
  }, []);
}

/* =========================================================
   Tab config
   ========================================================= */
const TABS = [
  { key: "dashboard",   label: "Home",       icon: "🏠", sidebarLabel: "Dashboard" },
  { key: "marks",       label: "Marks",      icon: "✏️", sidebarLabel: "Enter Marks" },
  { key: "class_view",  label: "Class",      icon: "🏫", sidebarLabel: "Class Marks View" },
  { key: "ct_students", label: "Students",   icon: "👥", sidebarLabel: "My Class Students" },
  { key: "attendance",  label: "Attend.",    icon: "📅", sidebarLabel: "Attendance" },
  { key: "remarks",     label: "Remarks",    icon: "💬", sidebarLabel: "Remarks" },
  { key: "profile",     label: "Profile",    icon: "👤", sidebarLabel: "My Profile" },
];

const SIDEBAR_TABS = TABS.map((t) => ({ key: t.key, label: t.sidebarLabel }));

/* =========================================================
   Root layout
   ========================================================= */
export default function TeacherDashboard() {
  useInjectStyles();
  const [tab, setTab] = useState(() => sessionStorage.getItem("t_tab") || "dashboard");

  function navigate(t) {
    sessionStorage.setItem("t_tab", t);
    setTab(t);
  }

  return (
    <div className="app-shell">
      {/* Sidebar — hidden on mobile via CSS */}
      <div className="td-sidebar-wrap">
        <Sidebar title="Teacher" tabs={SIDEBAR_TABS} activeTab={tab} onTabChange={navigate} />
      </div>

      {/* Main content */}
      <div className="main-content td-main">
        {tab === "dashboard"  && <TeacherHome onNavigate={navigate} />}
        {tab === "marks"      && <MarksEntry />}
        {tab === "class_view" && <ClassTeacherMarksView onNavigate={navigate} />}
        {tab === "ct_students" && <ClassStudents />}
        {tab === "attendance" && <Attendance />}
        {tab === "remarks"    && <Remarks />}
        {tab === "profile"    && <MyProfile />}
      </div>

      {/* Bottom nav — shown on mobile via CSS */}
      <nav className="td-bottom-nav">
        {TABS.map((t) => (
          <button key={t.key} className={tab === t.key ? "active" : ""} onClick={() => navigate(t.key)}>
            <span className="nav-icon">{t.icon}</span>
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

/* =========================================================
   Shared helpers
   ========================================================= */
function PageHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h1 className="td-section-title">{title}</h1>
      {subtitle && <p className="td-section-sub">{subtitle}</p>}
    </div>
  );
}

function Alert({ type = "success", children }) {
  const styles = {
    success: { bg: "#F0FDF4", border: "#BBF7D0", color: "#166534" },
    error:   { bg: "#FEF2F2", border: "#FECACA", color: "#991B1B" },
    warn:    { bg: "#FFFBEB", border: "#FCD34D", color: "#92400E" },
  }[type];
  return (
    <div style={{ background: styles.bg, border: `1px solid ${styles.border}`, color: styles.color,
      borderRadius: 10, padding: "10px 14px", fontSize: 14, marginBottom: 14 }}>
      {children}
    </div>
  );
}

function Spinner() {
  return <div style={{ textAlign: "center", color: "#94A3B8", padding: "32px 0", fontSize: 14 }}>Loading…</div>;
}

function EmptyState({ icon, title, sub }) {
  return (
    <div style={{ textAlign: "center", padding: "36px 16px" }}>
      <div style={{ fontSize: 38, marginBottom: 12 }}>{icon}</div>
      {title && <div style={{ fontWeight: 700, fontSize: 15, color: "#1E293B", marginBottom: 6 }}>{title}</div>}
      {sub && <div style={{ fontSize: 13, color: "#64748B", maxWidth: 380, margin: "0 auto" }}>{sub}</div>}
    </div>
  );
}

/* =========================================================
   Stat Card
   ========================================================= */
function StatCard({ icon, value, label, color, onClick }) {
  return (
    <div
      onClick={onClick}
      className={onClick ? "td-stat" : "td-stat-no-hover"}
      style={{
        background: "#fff", border: "1px solid #E8EDF5", borderRadius: 16,
        padding: "18px 20px", display: "flex", alignItems: "center", gap: 14,
        boxShadow: "0 1px 4px rgba(0,0,0,.04), 0 4px 16px rgba(0,0,0,.04)",
      }}
    >
      <div style={{
        width: 50, height: 50, borderRadius: 14, flexShrink: 0,
        background: `linear-gradient(135deg, ${color}33, ${color}18)`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 900, color: "#1E293B", lineHeight: 1 }}>{value ?? "—"}</div>
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 3, fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}

/* =========================================================
   Dashboard / Home
   ========================================================= */
function TeacherHome({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/teacher/dashboard")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;
  if (!data) return <Alert type="error">Failed to load dashboard. Please refresh.</Alert>;

  const { teacher, school, stats, assignments } = data;

  const byClass = {};
  assignments.forEach((a) => {
    const key = a.classSection?.id;
    if (!byClass[key]) byClass[key] = { classSection: a.classSection, subjects: [] };
    byClass[key].subjects.push(a);
  });

  const initials = teacher.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div>
      {/* Welcome header */}
      <div className="td-card" style={{ background: "linear-gradient(135deg, #1E3A5F 0%, #2D5082 100%)", border: "none", color: "#fff", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, background: "rgba(255,255,255,.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, fontWeight: 800, color: "#fff", flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, marginBottom: 3 }}>Welcome back</div>
            <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>{teacher.name}</div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 3 }}>
              {teacher.designation && <span>{teacher.designation}</span>}
              {teacher.designation && school && <span style={{ margin: "0 6px", opacity: 0.5 }}>·</span>}
              {school?.name}
            </div>
          </div>
          {teacher.mustResetPassword && (
            <button
              className="btn sm"
              style={{ background: "#FCD34D", color: "#92400E", border: "none", fontWeight: 700, flexShrink: 0 }}
              onClick={() => onNavigate("profile")}
            >
              ⚠ Change Password
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="td-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
        <StatCard icon="📚" value={stats.assignments} label="Assignments" color="#3B82F6" onClick={() => onNavigate("marks")} />
        <StatCard icon="✅" value={stats.marksEntered} label="Marks Entered" color="#10B981" />
        <StatCard icon="🟢" value={stats.openPanels} label="Open Panels" color="#F59E0B" onClick={() => onNavigate("marks")} />
      </div>

      {/* My Assignments */}
      <div className="td-card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#1E293B" }}>My Subject Assignments</div>
          <button className="btn sm" onClick={() => onNavigate("marks")}>Enter Marks →</button>
        </div>

        {assignments.length === 0 ? (
          <EmptyState icon="📌" title="No assignments yet" sub="Your school admin needs to assign you to subjects and classes." />
        ) : (
          <div className="td-assign-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
            {Object.values(byClass).map(({ classSection, subjects }) => (
              <div key={classSection?.id} style={{ border: "1px solid #E2E8F0", borderRadius: 14, overflow: "hidden" }}>
                <div style={{ background: "linear-gradient(135deg, #1E3A5F, #2D5082)", padding: "12px 16px", color: "#fff" }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>
                    {classSection?.name}{classSection?.section && ` — ${classSection.section}`}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                    {subjects.length} subject{subjects.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {subjects.map((a) => (
                    <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#1E293B" }}>{a.subject?.name}</div>
                        <div style={{ fontSize: 11, color: "#94A3B8" }}>
                          {a.subject?.category === "co_scholastic" ? "Co-Scholastic" : `Max: ${a.subject?.max_marks}`}
                        </div>
                      </div>
                      <span className={`td-pill ${a.accessType === "entry" ? "td-pill-green" : "td-pill-blue"}`}>
                        {a.accessType === "entry" ? "Entry" : "View"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Profile snippet */}
      <div className="td-card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#1E293B" }}>Quick Profile</div>
          <button className="btn sm secondary" onClick={() => onNavigate("profile")}>Edit →</button>
        </div>
        <div className="td-profile-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 }}>
          {[
            { label: "Login ID", value: teacher.username, mono: true },
            { label: "Email", value: teacher.email || "—" },
            { label: "Mobile", value: teacher.mobile || "—" },
            { label: "Qualification", value: teacher.qualification || "—" },
          ].map(({ label, value, mono }) => (
            <div key={label}>
              <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 3, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
              <div style={{ fontSize: 13, fontWeight: mono ? 700 : 500, fontFamily: mono ? "monospace" : "inherit", color: "#1E293B" }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   Enter Marks
   ========================================================= */
function MarksEntry() {
  const isMobile = useIsMobile();
  const [assignments, setAssignments] = useState([]);
  const [exams, setExams] = useState([]);
  const [selClassId, setSelClassId] = useState("");
  const [selSubjectId, setSelSubjectId] = useState("");
  const [selected, setSelected] = useState(null);
  const [examId, setExamId] = useState("");
  const [sheet, setSheet] = useState(null);
  const [sheetError, setSheetError] = useState("");
  const [remarks, setRemarks] = useState({});
  const [savingRemarkId, setSavingRemarkId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvError, setCsvError] = useState("");
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvSuccess, setCsvSuccess] = useState("");
  const csvInputRef = useRef(null);
  const [sortCol, setSortCol] = useState("roll");
  const [sortDir, setSortDir] = useState("asc");
  const [localMarks, setLocalMarks] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [savingIds, setSavingIds] = useState(new Set());
  const [saveStatus, setSaveStatus] = useState({});
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    api.get("/teacher/my-assignments").then((r) => setAssignments(r.data)).catch(() => {});
    api.get("/teacher/my-exams").then((r) => setExams(r.data)).catch(() => {});
  }, []);

  const loadSheet = useCallback(async () => {
    if (!selected || !examId) return;
    setSheetError(""); setRemarks({}); setLocalMarks({}); setDirty(new Set()); setLoading(true);
    try {
      const { data } = await api.get("/teacher/marks-sheet", {
        params: { subjectId: selected.subject_id, classSectionId: selected.class_section_id, examId },
      });
      setSheet(data);
      try {
        const rData = await api.get("/teacher/remarks", { params: { examId, classSectionId: selected.class_section_id } });
        const remarkMap = {};
        rData.data.forEach((r) => { remarkMap[r.student_id] = { value: r.remark || "", saving: false, expanded: false }; });
        setRemarks(remarkMap);
      } catch (_) {}
    } catch (err) {
      setSheetError(err.response?.data?.error || "Could not load marks sheet.");
      setSheet(null);
    } finally { setLoading(false); }
  }, [selected, examId]);

  useEffect(() => { if (selected && examId) loadSheet(); }, [selected, examId, loadSheet]);

  function onMarksChange(studentId, value) {
    setLocalMarks((p) => ({ ...p, [studentId]: value }));
    setDirty((p) => { const s = new Set(p); s.add(studentId); return s; });
    setSaveStatus((p) => ({ ...p, [studentId]: null }));
  }

  async function saveMark(row, value) {
    const coScho = selected?.Subject?.category === "co_scholastic";
    if (!coScho && value !== "") {
      const num = Number(value);
      if (isNaN(num) || num < 0) { alert("Marks must be a non-negative number."); return; }
      const maxM = selected?.Subject?.max_marks;
      if (maxM !== null && maxM !== undefined && num > maxM) {
        alert(`Marks cannot exceed maximum marks (${maxM}).`); return;
      }
    }
    const payload = {
      studentId: row.studentId, subjectId: selected.subject_id,
      examId, classSectionId: selected.class_section_id,
      marksObtained: coScho ? null : (value === "" ? null : Number(value)),
      gradeValue: coScho ? (value || null) : null,
    };
    setSavingIds((p) => { const s = new Set(p); s.add(row.studentId); return s; });
    try {
      await api.post("/teacher/marks", payload);
      setSaveStatus((p) => ({ ...p, [row.studentId]: "saved" }));
      setDirty((p) => { const s = new Set(p); s.delete(row.studentId); return s; });
      setSheet((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => r.studentId === row.studentId
          ? { ...r, marksObtained: payload.marksObtained, gradeValue: payload.gradeValue, isAbsent: false } : r),
      }));
      setTimeout(() => setSaveStatus((p) => ({ ...p, [row.studentId]: null })), 3000);
    } catch (err) {
      setSaveStatus((p) => ({ ...p, [row.studentId]: "error" }));
      alert(err.response?.data?.error || "Could not save mark. The panel may be closed.");
    } finally { setSavingIds((p) => { const s = new Set(p); s.delete(row.studentId); return s; }); }
  }

  async function saveAllMarks() {
    const dirtyRows = sheet.rows.filter((r) => dirty.has(r.studentId) && !r.isAbsent);
    if (!dirtyRows.length) return;
    setSavingAll(true);
    for (const row of dirtyRows) {
      const fallback = isCoScholastic ? (row.gradeValue ?? "") : String(row.marksObtained ?? "");
      await saveMark(row, localMarks[row.studentId] ?? fallback);
    }
    setSavingAll(false);
  }

  async function saveRemark(studentId) {
    const currentRemark = remarks[studentId]?.value || "";
    setSavingRemarkId(studentId);
    try {
      await api.post("/teacher/remarks", { studentId, examId: Number(examId), remark: currentRemark });
      setRemarks((prev) => ({ ...prev, [studentId]: { ...prev[studentId], saving: false } }));
    } catch (err) { alert(err.response?.data?.error || "Could not save remark."); }
    finally { setSavingRemarkId(null); }
  }

  function toggleRemarkExpand(studentId) {
    setRemarks((prev) => ({
      ...prev,
      [studentId]: { value: prev[studentId]?.value || "", saving: false, expanded: !prev[studentId]?.expanded },
    }));
  }

  function updateRemarkValue(studentId, value) {
    setRemarks((prev) => ({ ...prev, [studentId]: { ...prev[studentId], value } }));
  }

  function downloadSampleCsv() {
    if (!sheet) return;
    const examName = exams.find((e) => String(e.id) === String(examId))?.name || `exam${examId}`;
    const subjectName = selected?.Subject?.name || "subject";
    const className = `${selected?.ClassSection?.name || ""}${selected?.ClassSection?.section || ""}`;
    const header = "roll_no,student_name,marks_obtained";
    const rows = sheet.rows.map((r) =>
      `${r.rollNo},"${r.name.replace(/"/g, '""')}",${r.marksObtained !== null && r.marksObtained !== undefined ? r.marksObtained : ""}`
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `marks_${subjectName}_${className}_${examName}.csv`.replace(/\s+/g, "_");
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleCsvFileChange(e) {
    // Supports both real <input> events and synthetic drag-drop events
    // where result is already pre-read (passed as e.target.result)
    if (e.target?.result !== undefined) {
      parseCsvText(e.target.result);
      return;
    }
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = (ev) => parseCsvText(ev.target.result);
    reader.readAsText(file);
  }

  function parseCsvText(text) {
    setCsvError(""); setCsvPreview(null); setCsvSuccess("");
    try {
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) { setCsvError("CSV must have a header row and at least one data row."); return; }
        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ""));
        const rollIdx = headers.indexOf("roll_no");
        const marksIdx = headers.indexOf("marks_obtained");
        if (rollIdx === -1 || marksIdx === -1) {
          setCsvError("CSV must have columns: roll_no, marks_obtained"); return;
        }
        const rollMap = {};
        sheet.rows.forEach((r) => { rollMap[String(r.rollNo)] = r; });
        const parsed = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
          const rollNo = cols[rollIdx];
          const marksRaw = cols[marksIdx];
          const student = rollMap[rollNo];
          if (!student) { parsed.push({ rollNo, name: "— not found —", studentId: null, marksObtained: marksRaw, error: true }); continue; }
          const marksVal = marksRaw === "" ? null : Number(marksRaw);
          if (marksRaw !== "" && isNaN(marksVal)) { parsed.push({ rollNo, name: student.name, studentId: student.studentId, marksObtained: marksRaw, error: true, errorMsg: "Invalid number" }); continue; }
          parsed.push({ rollNo, name: student.name, studentId: student.studentId, marksObtained: marksVal, error: false });
        }
        setCsvPreview(parsed);
      } catch (err) { setCsvError("Could not parse CSV file."); }
  }

  async function confirmCsvUpload() {
    if (!csvPreview) return;
    const validRows = csvPreview.filter((r) => !r.error && r.studentId !== null);
    if (validRows.length === 0) { setCsvError("No valid rows to upload."); return; }
    setCsvUploading(true); setCsvError(""); setCsvSuccess("");
    try {
      const { data } = await api.post("/teacher/marks/bulk", {
        subjectId: selected.subject_id,
        classSectionId: selected.class_section_id,
        examId,
        records: validRows.map((r) => ({ studentId: r.studentId, marksObtained: r.marksObtained })),
      });
      setCsvSuccess(data.message || "Marks uploaded successfully.");
      setCsvPreview(null);
      loadSheet();
    } catch (err) {
      setCsvError(err.response?.data?.error || "Upload failed.");
    } finally { setCsvUploading(false); }
  }

  const canEdit = sheet && sheet.accessType !== "view" && sheet.panel?.status === "open";
  const panelStatus = sheet?.panel?.status;
  const panelPillClass = panelStatus === "open" ? "td-pill-green" : panelStatus === "closed" ? "td-pill-red" : "td-pill-amber";
  const isCoScholastic = selected?.Subject?.category === "co_scholastic";
  const gradeScale = isCoScholastic ? (Array.isArray(selected.Subject.grade_scale) ? selected.Subject.grade_scale : (() => { try { return JSON.parse(selected.Subject.grade_scale || "[]"); } catch { return []; } })()) : [];

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const sortedRows = (() => {
    if (!sheet?.rows) return [];
    const rows = [...sheet.rows];
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortCol === "name") return rows.sort((a, b) => dir * a.name.localeCompare(b.name));
    return rows.sort((a, b) => {
      const ra = parseInt(a.rollNo) || 0, rb = parseInt(b.rollNo) || 0;
      return dir * (ra !== rb ? ra - rb : (a.rollNo || "").localeCompare(b.rollNo || ""));
    });
  })();

  async function toggleAbsent(row) {
    if (!canEdit) return;
    const nowAbsent = !row.isAbsent;
    setSavingIds((p) => { const s = new Set(p); s.add(row.studentId); return s; });
    try {
      await api.post("/teacher/marks", {
        studentId: row.studentId, subjectId: selected.subject_id,
        examId: Number(examId), classSectionId: selected.class_section_id,
        marksObtained: null, gradeValue: nowAbsent ? "AB" : null,
      });
      setSaveStatus((p) => ({ ...p, [row.studentId]: "saved" }));
      setDirty((p) => { const s = new Set(p); s.delete(row.studentId); return s; });
      setLocalMarks((p) => { const m = { ...p }; delete m[row.studentId]; return m; });
      setSheet((prev) => ({
        ...prev,
        rows: prev.rows.map((r) => r.studentId === row.studentId
          ? { ...r, isAbsent: nowAbsent, marksObtained: null, gradeValue: nowAbsent ? "AB" : null } : r),
      }));
      setTimeout(() => setSaveStatus((p) => ({ ...p, [row.studentId]: null })), 3000);
    } catch (err) {
      alert(err.response?.data?.error || "Could not update absent status.");
    } finally { setSavingIds((p) => { const s = new Set(p); s.delete(row.studentId); return s; }); }
  }

  // Unique classes from assignments (preserving order)
  const uniqueClasses = assignments.reduce((acc, a) => {
    if (!acc.find((c) => c.id === a.class_section_id)) {
      acc.push({ id: a.class_section_id, name: `${a.ClassSection?.name || ""} ${a.ClassSection?.section || ""}`.trim() });
    }
    return acc;
  }, []);

  // Subjects available for the selected class
  const subjectsForClass = selClassId
    ? assignments.filter((a) => String(a.class_section_id) === String(selClassId))
    : [];

  function onClassChange(classId) {
    setSelClassId(classId);
    setSelSubjectId("");
    setSelected(null);
    setSheet(null); setSheetError(""); setRemarks({});
  }

  function onSubjectChange(subjectId) {
    setSelSubjectId(subjectId);
    const match = assignments.find(
      (a) => String(a.class_section_id) === String(selClassId) && String(a.subject_id) === String(subjectId)
    ) || null;
    setSelected(match);
    setSheet(null); setSheetError(""); setRemarks({});
  }

  return (
    <div>
      <PageHeader title="Enter Marks" subtitle="Select class, subject and exam, then enter marks for each student." />

      {/* Selector */}
      <div className="td-card">
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 14 }}>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Class / Section</label>
            <select value={selClassId} onChange={(e) => onClassChange(e.target.value)}>
              <option value="">— Select class —</option>
              {uniqueClasses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Subject</label>
            <select value={selSubjectId} onChange={(e) => onSubjectChange(e.target.value)} disabled={!selClassId}>
              <option value="">— Select subject —</option>
              {subjectsForClass.map((a) => (
                <option key={a.subject_id} value={a.subject_id}>
                  {a.Subject?.name}{a.access_type === "view" ? " (View only)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="form-field" style={{ margin: 0 }}>
            <label className="form-label">Exam</label>
            <select value={examId} onChange={(e) => { setExamId(e.target.value); setSheet(null); setSheetError(""); setRemarks({}); }} disabled={!selected}>
              <option value="">— Select exam —</option>
              {exams.map((ex) => {
                const p = selected ? ex.panels.find((p) => p.classId === selected.class_section_id) : null;
                return (
                  <option key={ex.id} value={ex.id}>
                    {ex.name} ({ex.session}){p ? ` [${p.status}]` : ""}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        {sheetError && <Alert type="error" style={{ marginTop: 12 }}>{sheetError}</Alert>}
      </div>

      {loading && <div className="td-card"><Spinner /></div>}

      {sheet && !loading && (
        <div className="td-card">
          {/* Card header */}
          <div className="td-card-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, gap: 12 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#1E293B" }}>
                {selected?.Subject?.name} — {selected?.ClassSection?.name} {selected?.ClassSection?.section}
              </div>
              <div style={{ fontSize: 13, color: "#64748B", marginTop: 3 }}>
                {exams.find((e) => String(e.id) === String(examId))?.name || `Exam #${examId}`}
                &nbsp;·&nbsp;{sheet.rows?.length || 0} students
              </div>
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                <span className={`td-pill ${panelPillClass}`}>Panel: {panelStatus || "not configured"}</span>
                {sheet.accessType === "view" && <span className="td-pill td-pill-amber">View only</span>}
              </div>
            </div>
            <div className="td-sheet-actions" style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap", alignItems: "center" }}>
              {canEdit && dirty.size > 0 && (
                <button
                  type="button"
                  className="btn sm"
                  onClick={saveAllMarks}
                  disabled={savingAll}
                  style={{ background: "#16A34A", borderColor: "#16A34A", fontWeight: 700 }}
                >
                  {savingAll ? "Saving…" : `💾 Save All (${dirty.size})`}
                </button>
              )}
              <button type="button" className="btn sm secondary" onClick={downloadSampleCsv}>⬇ Sample CSV</button>
              {["CSV", "Excel", "PDF"].map((fmt) => (
                <button key={fmt} type="button" className="btn sm secondary" style={{ fontSize: 11 }}
                  onClick={() => {
                    const subjectName = selected?.Subject?.name || "Subject";
                    const className = `${selected?.ClassSection?.name || ""} ${selected?.ClassSection?.section || ""}`.trim();
                    const examName = exams.find((e) => String(e.id) === String(examId))?.name || `Exam #${examId}`;
                    const title = `${subjectName} — ${className} — ${examName}`;
                    const headers = ["Roll", "Student Name", isCoScholastic ? "Grade" : `Marks/${selected?.Subject?.max_marks ?? "?"}`, "Status"];
                    const rows = sortedRows.map((r) => {
                      const val = localMarks[r.studentId] ?? (isCoScholastic ? (r.gradeValue ?? "") : (r.marksObtained !== null ? String(r.marksObtained) : ""));
                      return [r.rollNo ?? "—", r.name, r.isAbsent ? "AB" : (val !== "" ? val : "—"), r.isAbsent ? "Absent" : (val !== "" ? "Entered" : "Not entered")];
                    });
                    const slug = `${subjectName}_${className}_${examName}_marks`.replace(/\s+/g, "_");
                    if (fmt === "CSV")   exportCSV(`${slug}.csv`, headers, rows);
                    if (fmt === "Excel") exportExcel(`${slug}.xls`, title, headers, rows);
                    if (fmt === "PDF")   exportPDF(title, headers, rows);
                  }}
                >
                  ⬇ {fmt}
                </button>
              ))}
            </div>
          </div>

          {!canEdit && panelStatus !== "open" && (
            <Alert type="warn">
              The marks-entry panel is <strong>{panelStatus || "not configured"}</strong>. You can view marks but cannot edit. Contact your school admin to reopen.
            </Alert>
          )}

          {sortedRows.length === 0 ? (
            <EmptyState icon="👥" title="No students in this class." />
          ) : (
            <>
              {/* Desktop table */}
              <div className="td-marks-desktop" style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 60, textAlign: "center", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }} onClick={() => toggleSort("roll")}>
                        Roll {sortCol === "roll" ? (sortDir === "asc" ? "↑" : "↓") : <span style={{ color: "#CBD5E1" }}>↕</span>}
                      </th>
                      <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("name")}>
                        Student {sortCol === "name" ? (sortDir === "asc" ? "↑" : "↓") : <span style={{ color: "#CBD5E1" }}>↕</span>}
                      </th>
                      <th style={{ width: 140 }}>Marks</th>
                      <th style={{ width: 70, textAlign: "center" }}>Absent</th>
                      <th style={{ width: 90 }}>Status</th>
                      <th>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row) => {
                      const rs = remarks[row.studentId] || { value: "", saving: false, expanded: false };
                      const isSaving = savingIds.has(row.studentId);
                      const isDirty = dirty.has(row.studentId);
                      const status = saveStatus[row.studentId];
                      const currentVal = localMarks[row.studentId] ?? (isCoScholastic ? (row.gradeValue ?? "") : (row.marksObtained !== null ? String(row.marksObtained) : ""));
                      const hasEntry = isCoScholastic ? !!currentVal : row.marksObtained !== null;
                      return (
                        <React.Fragment key={row.studentId}>
                          <tr style={{ background: row.isAbsent ? "#FFFBEB" : isDirty ? "#F0FDF4" : (hasEntry ? "#fff" : "#FFFBF0") }}>
                            <td style={{ textAlign: "center", fontWeight: 700 }}>{row.rollNo}</td>
                            <td><span style={{ fontWeight: 600 }}>{row.name}</span></td>
                            <td>
                              {row.isAbsent
                                ? <span style={{ color: "#D97706", fontWeight: 700, fontSize: 13 }}>Absent</span>
                                : isCoScholastic
                                  ? <select
                                      value={currentVal}
                                      disabled={!canEdit || isSaving}
                                      onChange={(e) => { canEdit && onMarksChange(row.studentId, e.target.value); canEdit && saveMark(row, e.target.value); }}
                                      style={{
                                        width: 110,
                                        border: `2px solid ${isDirty ? "#86EFAC" : "var(--border)"}`,
                                        background: !canEdit ? "var(--bg)" : isDirty ? "#F0FDF4" : "#fff",
                                        cursor: !canEdit ? "not-allowed" : "pointer",
                                        padding: "4px 6px", borderRadius: 6,
                                      }}
                                    >
                                      <option value="">— Grade —</option>
                                      {gradeScale.map((g) => (
                                        <option key={g.grade} value={g.grade}>{g.grade}{g.label ? ` — ${g.label}` : ""}</option>
                                      ))}
                                    </select>
                                  : <input
                                      type="number"
                                      value={currentVal}
                                      disabled={!canEdit || isSaving}
                                      onChange={(e) => canEdit && onMarksChange(row.studentId, e.target.value)}
                                      onKeyDown={(e) => canEdit && e.key === "Enter" && saveMark(row, currentVal)}
                                      style={{
                                        width: 90,
                                        border: `2px solid ${isDirty ? "#86EFAC" : "var(--border)"}`,
                                        background: !canEdit ? "var(--bg)" : isDirty ? "#F0FDF4" : "#fff",
                                        cursor: !canEdit ? "not-allowed" : "text",
                                      }}
                                      placeholder="—"
                                    />
                              }
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={!!row.isAbsent}
                                disabled={!canEdit || isSaving}
                                onChange={() => toggleAbsent(row)}
                                style={{ width: 16, height: 16, cursor: canEdit ? "pointer" : "not-allowed", accentColor: "#D97706" }}
                                title="Mark as absent"
                              />
                            </td>
                            <td>
                              {isSaving
                                ? <span className="badge info">Saving…</span>
                                : isDirty && canEdit
                                  ? <button
                                      type="button"
                                      className="btn sm"
                                      onClick={() => saveMark(row, currentVal)}
                                      style={{ fontSize: 12, padding: "3px 10px", background: "#16A34A", borderColor: "#16A34A" }}
                                    >💾 Save</button>
                                  : status === "saved"
                                    ? <span className="badge active">✓ Saved</span>
                                    : status === "error"
                                      ? <span className="badge danger">✕ Error</span>
                                      : row.isAbsent
                                        ? <span className="badge warn">Absent</span>
                                        : (isCoScholastic ? !!row.gradeValue : row.marksObtained !== null)
                                          ? <span className="badge active">Entered</span>
                                          : <span className="badge warn">Pending</span>
                              }
                            </td>
                            <td>
                              <button type="button" className="btn sm secondary" onClick={() => toggleRemarkExpand(row.studentId)} style={{ fontSize: 12 }}>
                                {rs.expanded ? "Close" : rs.value ? "Edit" : "+ Remark"}
                              </button>
                              {rs.value && !rs.expanded && (
                                <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: 6, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", verticalAlign: "middle", whiteSpace: "nowrap" }}>
                                  {rs.value}
                                </span>
                              )}
                            </td>
                          </tr>
                          {rs.expanded && (
                            <tr>
                              <td colSpan={5} style={{ background: "var(--bg)", padding: "8px 16px" }}>
                                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                                  <textarea
                                    value={rs.value}
                                    onChange={(e) => updateRemarkValue(row.studentId, e.target.value)}
                                    placeholder="Write remark for this student…"
                                    rows={2}
                                    style={{ flex: 1, resize: "vertical", fontSize: 13 }}
                                  />
                                  <button type="button" className="btn sm" disabled={savingRemarkId === row.studentId} onClick={() => saveRemark(row.studentId)}>
                                    {savingRemarkId === row.studentId ? "…" : "Save"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile card list */}
              <div className="td-marks-mobile" style={{ flexDirection: "column", gap: 10 }}>
                {sortedRows.map((row) => {
                  const rs = remarks[row.studentId] || { value: "", saving: false, expanded: false };
                  const isSaving = savingIds.has(row.studentId);
                  const isDirty = dirty.has(row.studentId);
                  const status = saveStatus[row.studentId];
                  const currentVal = localMarks[row.studentId] ?? (row.marksObtained !== null ? String(row.marksObtained) : "");
                  const entered = row.marksObtained !== null;
                  return (
                    <div key={row.studentId} className={`td-student-card ${row.isAbsent ? "pending" : entered ? "entered" : "pending"}`}
                      style={{ background: row.isAbsent ? "#FFFBEB" : isDirty ? "#F0FDF4" : undefined }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: "#1E293B" }}>{row.name}</div>
                          <div style={{ fontSize: 12, color: "#94A3B8" }}>Roll No. {row.rollNo}</div>
                        </div>
                        {isSaving
                          ? <span className="td-pill td-pill-blue">Saving…</span>
                          : row.isAbsent ? <span className="td-pill td-pill-amber">Absent</span>
                          : isDirty ? <span className="td-pill td-pill-amber">Unsaved</span>
                          : entered ? <span className="td-pill td-pill-green">✓ Entered</span>
                          : <span className="td-pill td-pill-amber">Pending</span>
                        }
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                          <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Marks Obtained</label>
                          {canEdit && (
                            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: row.isAbsent ? "#D97706" : "#64748B", cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={!!row.isAbsent}
                                disabled={isSaving}
                                onChange={() => toggleAbsent(row)}
                                style={{ width: 15, height: 15, accentColor: "#D97706" }}
                              />
                              Absent
                            </label>
                          )}
                        </div>
                        {row.isAbsent
                          ? <div style={{ textAlign: "center", fontSize: 18, fontWeight: 700, color: "#D97706", padding: "10px", borderRadius: 10, background: "#FEF3C7" }}>Absent</div>
                          : <input
                              type="number"
                              value={currentVal}
                              disabled={!canEdit || isSaving}
                              onChange={(e) => canEdit && onMarksChange(row.studentId, e.target.value)}
                              style={{ width: "100%", fontSize: 18, fontWeight: 700, textAlign: "center", padding: "10px", borderRadius: 10,
                                border: `2px solid ${isDirty ? "#86EFAC" : "#E2E8F0"}`,
                                background: !canEdit ? "#F8FAFC" : isDirty ? "#F0FDF4" : "#fff",
                                cursor: !canEdit ? "not-allowed" : "text" }}
                              placeholder="—"
                            />
                        }
                        {canEdit && isDirty && !row.isAbsent && (
                          <button
                            type="button"
                            className="btn"
                            disabled={isSaving}
                            onClick={() => saveMark(row, currentVal)}
                            style={{ marginTop: 8, width: "100%", background: "#16A34A", borderColor: "#16A34A", fontWeight: 700 }}
                          >
                            {isSaving ? "Saving…" : "💾 Save"}
                          </button>
                        )}
                        {!isDirty && status === "saved" && (
                          <div style={{ textAlign: "center", marginTop: 6, color: "#16A34A", fontWeight: 700, fontSize: 13 }}>✓ Saved</div>
                        )}
                      </div>
                      <div>
                        <button type="button" className="btn sm secondary" onClick={() => toggleRemarkExpand(row.studentId)} style={{ width: "100%", fontSize: 12 }}>
                          {rs.expanded ? "▲ Close Remark" : rs.value ? "✎ Edit Remark" : "+ Add Remark"}
                        </button>
                        {rs.expanded && (
                          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                            <textarea
                              value={rs.value}
                              onChange={(e) => updateRemarkValue(row.studentId, e.target.value)}
                              placeholder="Write remark…"
                              rows={2}
                              style={{ width: "100%", fontSize: 13, resize: "vertical" }}
                            />
                            <button type="button" className="btn sm" disabled={savingRemarkId === row.studentId} onClick={() => saveRemark(row.studentId)}>
                              {savingRemarkId === row.studentId ? "Saving…" : "Save Remark"}
                            </button>
                          </div>
                        )}
                        {rs.value && !rs.expanded && (
                          <div style={{ marginTop: 6, fontSize: 12, color: "#64748B", background: "#F8FAFC", padding: "6px 10px", borderRadius: 8 }}>
                            {rs.value}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <p style={{ fontSize: 12, color: "#94A3B8", marginTop: 12 }}>
            Enter marks and click 💾 Save per row, or use Save All. Press Enter to save a row quickly.
          </p>
        </div>
      )}

      {/* ── CSV Upload Section ── always visible once a sheet is loaded ── */}
      {sheet && !loading && (
        <div className="td-card" style={{ marginTop: 4 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#1E293B" }}>📄 Upload Marks via CSV</div>
              <div style={{ fontSize: 13, color: "#64748B", marginTop: 3 }}>
                Download the sample, fill in marks, then upload. Roll numbers must match exactly.
              </div>
            </div>
            <button type="button" className="btn sm secondary" onClick={downloadSampleCsv}>
              ⬇ Download Sample CSV
            </button>
          </div>

          {!canEdit && (
            <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#92400E" }}>
              ⚠️ The marks panel is <strong>{panelStatus || "not configured"}</strong>. CSV upload is disabled. Ask the school admin to open the panel.
            </div>
          )}

          {canEdit && (
            <>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={handleCsvFileChange}
              />
              <div style={{
                border: "2px dashed #C4B5FD", borderRadius: 12, padding: "24px 20px",
                textAlign: "center", cursor: "pointer", background: "#FAFAFF",
                transition: "border-color .15s",
              }}
                onClick={() => { setCsvPreview(null); setCsvError(""); setCsvSuccess(""); csvInputRef.current?.click(); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => parseCsvText(ev.target.result);
                    reader.readAsText(file);
                  }
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#6D28D9", marginBottom: 4 }}>Click to choose CSV file</div>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>or drag and drop here · Required columns: <strong>roll_no</strong>, <strong>marks_obtained</strong></div>
              </div>
            </>
          )}

          {csvError && <Alert type="error" style={{ marginTop: 12 }}>{csvError}</Alert>}
          {csvSuccess && <Alert type="success" style={{ marginTop: 12 }}>{csvSuccess}</Alert>}

          {csvPreview && (
            <div style={{ marginTop: 16, border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
              <div className="td-csv-header" style={{ background: "#F8FAFC", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, borderBottom: "1px solid #E2E8F0", flexWrap: "wrap" }}>
                <div>
                  <strong style={{ fontSize: 14 }}>Preview — {csvPreview.length} rows</strong>
                  <span style={{ marginLeft: 10, fontSize: 12 }}>
                    <span style={{ color: "#16A34A", fontWeight: 600 }}>{csvPreview.filter((r) => !r.error).length} valid</span>
                    {csvPreview.some((r) => r.error) && <span style={{ color: "#DC2626", fontWeight: 600 }}> · {csvPreview.filter((r) => r.error).length} errors</span>}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" className="btn sm secondary" onClick={() => { setCsvPreview(null); setCsvError(""); }}>Cancel</button>
                  <button
                    type="button" className="btn sm"
                    disabled={csvUploading || csvPreview.every((r) => r.error)}
                    onClick={confirmCsvUpload}
                    style={{ background: "#16A34A", borderColor: "#16A34A" }}
                  >
                    {csvUploading ? "Uploading…" : `✅ Upload ${csvPreview.filter((r) => !r.error).length} rows`}
                  </button>
                </div>
              </div>
              <div style={{ overflowX: "auto", maxHeight: 340 }}>
                <table style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 70, textAlign: "center" }}>Roll No</th>
                      <th>Student Name</th>
                      <th style={{ width: 110, textAlign: "center" }}>Marks</th>
                      <th style={{ width: 100, textAlign: "center" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.map((row, i) => (
                      <tr key={i} style={{ background: row.error ? "#FFF7F7" : "#F0FDF4" }}>
                        <td style={{ textAlign: "center", fontWeight: 600 }}>{row.rollNo}</td>
                        <td style={{ fontWeight: row.error ? 400 : 600, color: row.error ? "#94A3B8" : undefined }}>{row.name}</td>
                        <td style={{ textAlign: "center", fontWeight: 700, fontSize: 15 }}>
                          {row.marksObtained !== null && row.marksObtained !== undefined
                            ? row.marksObtained
                            : <span style={{ color: "#94A3B8" }}>—</span>}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {row.error
                            ? <span className="td-pill td-pill-red">{row.errorMsg || "Not found"}</span>
                            : <span className="td-pill td-pill-green">✓ Ready</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {csvPreview.some((r) => r.error) && (
                <p style={{ margin: "8px 14px 12px", fontSize: 12, color: "#94A3B8" }}>
                  Rows with errors will be skipped. Verify roll numbers match exactly.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {!selected && assignments.length === 0 && (
        <div className="td-card">
          <EmptyState icon="📌" title="No assignments yet" sub="Contact your school admin to get assigned to subjects and classes." />
        </div>
      )}
    </div>
  );
}

/* =========================================================
   Class Teacher Marks View
   — See all subjects' marks for the class; enter marks only for assigned subjects
   ========================================================= */
function ClassTeacherMarksView({ onNavigate }) {
  const [assignments, setAssignments] = useState([]);
  const [exams, setExams] = useState([]);
  const [selClass, setSelClass] = useState("");
  const [selExam, setSelExam] = useState("");
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingCell, setSavingCell] = useState(null);
  const [saveStatus, setSaveStatus] = useState({});
  const [notCT, setNotCT] = useState(false);
  const [sortCol, setSortCol] = useState("rank");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    api.get("/teacher/my-assignments").then((r) => setAssignments(r.data)).catch(() => {});
    api.get("/teacher/my-exams").then((r) => setExams(r.data)).catch(() => {});
  }, []);

  const ctClasses = assignments
    .filter((a) => a.is_class_teacher)
    .reduce((acc, a) => {
      const key = a.class_section_id;
      if (!acc.find((x) => x.id === key)) acc.push({ id: key, name: a.ClassSection?.name, section: a.ClassSection?.section });
      return acc;
    }, []);

  useEffect(() => {
    if (!selClass || !selExam) { setSheet(null); setNotCT(false); return; }
    setLoading(true); setSheet(null); setNotCT(false);
    api.get("/teacher/class-teacher/marks-view", { params: { classSectionId: selClass, examId: selExam } })
      .then((r) => setSheet(r.data))
      .catch((err) => {
        if (err.response?.status === 403) setNotCT(true);
        else alert(err.response?.data?.error || "Failed to load marks.");
      })
      .finally(() => setLoading(false));
  }, [selClass, selExam]);

  async function saveMark(studentId, subjectId, value, isCoScholastic = false) {
    const key = `${studentId}-${subjectId}`;
    const v = typeof value === "string" ? value.trim().toUpperCase() : String(value ?? "");
    const isAbsentMark = v === "AB";
    const parsed = isCoScholastic || isAbsentMark ? null : (v === "" ? null : Number(v));
    const gradeValue = isAbsentMark ? "AB" : (isCoScholastic ? (v || null) : null);
    setSavingCell(key);
    try {
      await api.post("/teacher/marks", {
        studentId, subjectId, examId: Number(selExam), classSectionId: Number(selClass),
        marksObtained: parsed, gradeValue,
      });
      setSaveStatus((p) => ({ ...p, [key]: "saved" }));
      setTimeout(() => setSaveStatus((p) => ({ ...p, [key]: null })), 2000);
      // Update local sheet marks + recompute totals + ranks
      setSheet((prev) => {
        if (!prev) return prev;
        const scholastic = prev.subjects.filter((s) => s.category === "scholastic");
        const newRows = prev.rows.map((r) => {
          if (r.studentId !== studentId) return r;
          const newMarks = { ...r.marks, [subjectId]: v === "AB" ? "AB" : parsed };
          let totalObtained = 0;
          const totalMax = scholastic.reduce((sum, s) => sum + (Number(s.max_marks) || 0), 0);
          let overallPass = true;
          let hasAllMarks = true;
          scholastic.forEach((sub) => {
            const mv = newMarks[sub.id];
            if (mv === undefined || mv === null) { overallPass = false; hasAllMarks = false; return; }
            if (mv === "AB") { overallPass = false; return; }
            const obt = Number(mv) || 0;
            totalObtained += obt;
            if (obt < Number(sub.pass_marks)) overallPass = false;
          });
          const percentage = totalMax > 0 ? Math.round((totalObtained / totalMax) * 10000) / 100 : 0;
          return { ...r, marks: newMarks, totalObtained, totalMax, percentage, overallPass, hasAllMarks };
        });
        // Recompute ranks (respect rankPassedOnly setting)
        const rankPassedOnly = prev.rankPassedOnly || false;
        const rankable = rankPassedOnly
          ? newRows.filter((r) => r.overallPass && r.hasAllMarks)
          : [...newRows];
        const ranked = [...rankable].sort((a, b) => b.percentage - a.percentage);
        let rank = 1;
        ranked.forEach((r, i) => {
          if (i > 0 && ranked[i - 1].percentage !== r.percentage) rank = i + 1;
          r.rank = rank;
        });
        // Students not in rankable keep rank 0
        if (rankPassedOnly) {
          const rankableIds = new Set(rankable.map((r) => r.studentId));
          newRows.forEach((r) => { if (!rankableIds.has(r.studentId)) r.rank = 0; });
        }
        return { ...prev, rows: newRows };
      });
    } catch (err) {
      setSaveStatus((p) => ({ ...p, [key]: "error" }));
      alert(err.response?.data?.error || "Could not save mark.");
    } finally { setSavingCell(null); }
  }

  function toggleSort(col) {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const assignedSet = new Set(sheet?.assignedSubjectIds?.map(Number) || []);

  const sortedRows = (() => {
    if (!sheet?.rows) return [];
    const rows = [...sheet.rows];
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortCol === "name") return rows.sort((a, b) => dir * a.name.localeCompare(b.name));
    if (sortCol === "pct")  return rows.sort((a, b) => dir * (a.percentage - b.percentage));
    if (sortCol === "roll") return rows.sort((a, b) => dir * ((parseInt(a.rollNo) || 0) - (parseInt(b.rollNo) || 0)));
    return rows.sort((a, b) => dir * (a.rank - b.rank)); // default: rank
  })();

  const SortTh = ({ col, label, style = {} }) => (
    <th style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...style }} onClick={() => toggleSort(col)}>
      {label} {sortCol === col ? (sortDir === "asc" ? "↑" : "↓") : <span style={{ color: "#CBD5E1" }}>↕</span>}
    </th>
  );

  const gradeColor = (g) => {
    if (!g) return {};
    if (g === "A1" || g === "A2") return { color: "#15803D", fontWeight: 700 };
    if (g === "B1" || g === "B2") return { color: "#1D4ED8", fontWeight: 700 };
    if (g === "C1" || g === "C2") return { color: "#D97706", fontWeight: 700 };
    if (g === "D") return { color: "#EA580C", fontWeight: 700 };
    return { color: "#DC2626", fontWeight: 700 };
  };

  return (
    <div className="td-card">
      <div style={{ marginBottom: 20 }}>
        <div className="td-section-title">Class Marks View</div>
        <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>
          View all subjects' marks, totals, percentage, grade and rank for your class. ✏️ subjects are yours to edit.
        </div>
      </div>

      {ctClasses.length === 0 ? (
        <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 10, padding: "16px 20px", color: "#92400E", fontSize: 14 }}>
          You are not assigned as class teacher for any class. Ask your school admin to set you as class teacher.
        </div>
      ) : (
        <>
          {/* Selectors */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Your Class</label>
              <select value={selClass} onChange={(e) => setSelClass(e.target.value)} style={{ width: "100%" }}>
                <option value="">— Select Class —</option>
                {ctClasses.map((c) => <option key={c.id} value={c.id}>{c.name}{c.section ? `-${c.section}` : ""}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>Exam</label>
              <select value={selExam} onChange={(e) => setSelExam(e.target.value)} style={{ width: "100%" }}>
                <option value="">— Select Exam —</option>
                {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
          </div>

          {loading && <div style={{ textAlign: "center", padding: 32, color: "#94A3B8" }}>Loading marks sheet…</div>}

          {notCT && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: 16, color: "#DC2626" }}>
              You are not the class teacher for this class.
            </div>
          )}

          {sheet && !loading && (
            <>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1E293B" }}>
                    {ctClasses.find((c) => String(c.id) === String(selClass))?.name}
                    {ctClasses.find((c) => String(c.id) === String(selClass))?.section
                      ? `-${ctClasses.find((c) => String(c.id) === String(selClass)).section}` : ""}
                    &nbsp;&mdash;&nbsp;
                    {exams.find((e) => String(e.id) === String(selExam))?.name}
                    <span style={{ fontSize: 13, fontWeight: 400, color: "#64748B", marginLeft: 8 }}>({sheet.rows.length} students)</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 3 }}>
                    Click column headers to sort. ✏️ = your subjects (editable on blur / Enter).
                  </div>
                  {/* Subject legend */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {sheet.subjects.map((sub) => (
                      <span key={sub.id} style={{
                        fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
                        background: assignedSet.has(sub.id) ? "#EDE9FE" : "#F1F5F9",
                        color: assignedSet.has(sub.id) ? "#7C3AED" : "#64748B",
                        border: `1px solid ${assignedSet.has(sub.id) ? "#C4B5FD" : "#E2E8F0"}`,
                      }}>
                        {sub.name}{assignedSet.has(sub.id) ? " ✏️" : ""}
                        <span style={{ fontWeight: 400, marginLeft: 4 }}>/{sub.category === "co_scholastic" ? "Grade" : (sub.max_marks ?? "?")}</span>
                      </span>
                    ))}
                  </div>
                </div>
                {/* Download buttons — top right */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#94A3B8" }}>Download</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {["CSV", "Excel", "PDF"].map((fmt) => (
                      <button key={fmt} type="button" className="btn sm secondary"
                        onClick={() => {
                          const cls = ctClasses.find((c) => String(c.id) === String(selClass));
                          const clsName = (cls?.name || "") + (cls?.section ? `-${cls.section}` : "");
                          const examName = exams.find((e) => String(e.id) === String(selExam))?.name || "Exam";
                          const title = `${clsName} — ${examName} — Class Marks`;
                          const headers = ["Rank", "Roll", "Student Name", ...sheet.subjects.map((s) => `${s.name}/${s.category === "co_scholastic" ? "Grade" : (s.max_marks ?? "?")}`), "Total Obtained", "Total Max", "Percentage", "Grade", "Result"];
                          const rows = sortedRows.map((r) => [
                            r.rank === 0 ? "—" : r.rank,
                            r.rollNo ?? "—",
                            r.name,
                            ...sheet.subjects.map((s) => r.marks[s.id] !== undefined && r.marks[s.id] !== null ? r.marks[s.id] : "—"),
                            r.totalObtained,
                            r.totalMax,
                            `${r.percentage.toFixed(1)}%`,
                            r.grade || "—",
                            !r.hasAllMarks ? "Pending" : r.overallPass ? "PASS" : "FAIL",
                          ]);
                          const slug = `${clsName}_${examName}_marks`.replace(/\s+/g, "_");
                          if (fmt === "CSV")   exportCSV(`${slug}.csv`, headers, rows);
                          if (fmt === "Excel") exportExcel(`${slug}.xls`, title, headers, rows);
                          if (fmt === "PDF")   exportPDF(title, headers, rows);
                        }}
                      >
                        ⬇ {fmt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {sheet.rows.length === 0 ? (
                <div style={{ textAlign: "center", color: "#94A3B8", padding: 40 }}>No students or marks found.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ fontSize: 13, minWidth: 700 }}>
                    <thead>
                      <tr>
                        <SortTh col="rank" label="Rank" style={{ width: 60, textAlign: "center", position: "sticky", left: 0, zIndex: 3, background: "#F8FAFC" }} />
                        <SortTh col="roll" label="Roll" style={{ width: 60, position: "sticky", left: 60, zIndex: 3, background: "#F8FAFC" }} />
                        <SortTh col="name" label="Student Name" style={{ minWidth: 160, position: "sticky", left: 120, zIndex: 3, background: "#F8FAFC", boxShadow: "2px 0 5px rgba(0,0,0,0.08)" }} />
                        {sheet.subjects.map((sub) => (
                          <th key={sub.id} style={{ minWidth: 80, textAlign: "center", whiteSpace: "nowrap" }}>
                            {sub.name}{assignedSet.has(sub.id) ? " ✏️" : ""}
                            <div style={{ fontSize: 10, fontWeight: 400, color: "#94A3B8" }}>/{sub.category === "co_scholastic" ? "Grade" : (sub.max_marks ?? "?")}</div>
                          </th>
                        ))}
                        <SortTh col="pct" label="Total" style={{ width: 100, textAlign: "center" }} />
                        <th style={{ width: 80, textAlign: "center" }}>%</th>
                        <th style={{ width: 70, textAlign: "center" }}>Grade</th>
                        <th style={{ width: 80, textAlign: "center" }}>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row) => {
                        const pct = row.percentage;
                        const rowBg = !row.hasAllMarks ? undefined : !row.overallPass ? "#FEF2F2" : pct >= 75 ? "#F0FDF4" : undefined;
                        return (
                          <tr key={row.studentId} style={{ background: rowBg }}>
                            <td style={{ textAlign: "center", position: "sticky", left: 0, zIndex: 1, background: rowBg || "#fff" }}>
                              {row.rank === 0
                                ? <span style={{ color: "#CBD5E1" }}>—</span>
                                : row.rank <= 3
                                  ? <span style={{ fontSize: 15 }}>{row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : "🥉"}</span>
                                  : <span style={{ fontWeight: 600, color: "#94A3B8" }}>{row.rank}</span>}
                            </td>
                            <td style={{ color: "#94A3B8", fontWeight: 500, position: "sticky", left: 60, zIndex: 1, background: rowBg || "#fff" }}>{row.rollNo ?? "—"}</td>
                            <td style={{ fontWeight: 600, position: "sticky", left: 120, zIndex: 1, background: rowBg || "#fff", boxShadow: "2px 0 5px rgba(0,0,0,0.08)" }}>{row.name}</td>
                            {sheet.subjects.map((sub) => {
                              const canEdit = assignedSet.has(sub.id);
                              const cellKey = `${row.studentId}-${sub.id}`;
                              const val = row.marks[sub.id];
                              const isSaving = savingCell === cellKey;
                              const status = saveStatus[cellKey];
                              const isAbsent = val === "AB";
                              const isFail = !isAbsent && val !== undefined && val !== null
                                && sub.category === "scholastic" && Number(val) < (Number(sub.pass_marks) || 0);
                              return (
                                <td key={sub.id} style={{ textAlign: "center", padding: "4px 6px" }}>
                                  {canEdit ? (
                                    <div style={{ position: "relative", display: "inline-block" }}>
                                      <input
                                        type="text"
                                        defaultValue={isAbsent ? "AB" : (val !== undefined && val !== null ? String(val) : "")}
                                        key={`${row.studentId}-${sub.id}-${val}`}
                                        disabled={isSaving}
                                        onBlur={(e) => {
                                          const nv = e.target.value.trim().toUpperCase();
                                          if (nv !== String(val ?? "").toUpperCase()) saveMark(row.studentId, sub.id, nv, sub.category === "co_scholastic");
                                        }}
                                        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                                        placeholder="—"
                                        style={{
                                          width: 64, textAlign: "center", fontWeight: 700, fontSize: 13,
                                          padding: "4px 3px", borderRadius: 7,
                                          border: `1px solid ${status === "error" ? "#FCA5A5" : isFail ? "#FCA5A5" : isAbsent ? "#FCD34D" : status === "saved" ? "#86EFAC" : "#C4B5FD"}`,
                                          background: isSaving ? "#F8FAFC" : isAbsent ? "#FFFBEB" : isFail ? "#FEF2F2" : "#FAFAFF",
                                          color: isAbsent ? "#D97706" : isFail ? "#DC2626" : "#1E293B",
                                        }}
                                      />
                                      {status === "saved" && <span style={{ position: "absolute", top: -6, right: -4, fontSize: 10, color: "#16A34A", fontWeight: 900 }}>✓</span>}
                                    </div>
                                  ) : (
                                    <span style={{
                                      display: "inline-block", minWidth: 40, padding: "4px 8px", borderRadius: 7,
                                      background: val !== undefined && val !== null ? (isFail ? "#FEF2F2" : "#F0FDF4") : "#F8FAFC",
                                      color: val !== undefined && val !== null ? (isFail ? "#DC2626" : "#15803D") : "#CBD5E1",
                                      fontWeight: val !== undefined && val !== null ? 700 : 400, fontSize: 13,
                                    }}>
                                      {val !== undefined && val !== null ? val : "—"}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                            <td style={{ textAlign: "center", fontWeight: 700 }}>
                              {row.totalObtained} <span style={{ color: "#94A3B8", fontWeight: 400 }}>/ {row.totalMax}</span>
                            </td>
                            <td style={{ textAlign: "center", fontWeight: 700, ...gradeColor(row.grade) }}>
                              {pct.toFixed(1)}%
                            </td>
                            <td style={{ textAlign: "center", fontWeight: 800, ...gradeColor(row.grade) }}>
                              {row.grade}
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span style={{
                                display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
                                background: !row.hasAllMarks ? "#F1F5F9" : row.overallPass ? "#D1FAE5" : "#FEE2E2",
                                color: !row.hasAllMarks ? "#64748B" : row.overallPass ? "#065F46" : "#991B1B",
                                border: `1px solid ${!row.hasAllMarks ? "#CBD5E1" : row.overallPass ? "#6EE7B7" : "#FCA5A5"}`,
                              }}>
                                {!row.hasAllMarks ? "Pending" : row.overallPass ? "PASS" : "FAIL"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: "#F8FAFC", fontWeight: 700, borderTop: "2px solid #E2E8F0" }}>
                        <td colSpan={3} style={{ padding: "10px 14px", fontSize: 13, position: "sticky", left: 0, zIndex: 1, background: "#F8FAFC", boxShadow: "2px 0 5px rgba(0,0,0,0.08)" }}>Class Average</td>
                        {sheet.subjects.map((sub) => {
                          const vals = sheet.rows.map((r) => r.marks[sub.id]).filter((v) => v !== undefined && v !== null && v !== "AB").map(Number);
                          const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : "—";
                          return <td key={sub.id} style={{ textAlign: "center", color: "#64748B" }}>{avg}</td>;
                        })}
                        <td style={{ textAlign: "center" }}>
                          {(sheet.rows.reduce((a, r) => a + r.totalObtained, 0) / (sheet.rows.length || 1)).toFixed(1)}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {(sheet.rows.reduce((a, r) => a + r.percentage, 0) / (sheet.rows.length || 1)).toFixed(1)}%
                        </td>
                        <td />
                        <td style={{ textAlign: "center", fontSize: 12, color: "#64748B" }}>
                          {sheet.rows.filter((r) => r.overallPass).length}/{sheet.rows.length} Pass
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              <p style={{ fontSize: 12, color: "#94A3B8", marginTop: 12 }}>
                Editable cells (✏️) save on blur/Enter. Type "AB" for absent. View-only subjects show marks entered by their assigned teacher.
              </p>
            </>
          )}

          {!loading && !sheet && !notCT && selClass && selExam && (
            <div style={{ textAlign: "center", color: "#94A3B8", padding: 32 }}>No data found.</div>
          )}
          {!loading && (!selClass || !selExam) && (
            <div style={{ textAlign: "center", color: "#94A3B8", padding: 32, fontSize: 14 }}>
              Select your class and an exam to view the full marks sheet.
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* =========================================================
   Class Students (Class Teacher only)
   ========================================================= */
const STUDENT_FIELDS = [
  { key: "name",          label: "Full Name",       required: true },
  { key: "roll_no",       label: "Roll No",         required: true },
  { key: "admission_no",  label: "Admission No",    required: false },
  { key: "dob",           label: "Date of Birth",   required: false, type: "date" },
  { key: "gender",        label: "Gender",          required: false, type: "select", options: ["", "Male", "Female", "Other"] },
  { key: "father_name",   label: "Father's Name",   required: false },
  { key: "mother_name",   label: "Mother's Name",   required: false },
  { key: "parent_name",   label: "Guardian Name",   required: false },
  { key: "mobile",        label: "Mobile",          required: false },
  { key: "email",         label: "Email",           required: false },
];

const BLANK_FORM = Object.fromEntries(STUDENT_FIELDS.map((f) => [f.key, ""]));

function ClassStudents() {
  const [ctClasses, setCtClasses]     = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    api.get("/teacher/my-assignments").then((r) => {
      const seen = new Set(); const list = [];
      r.data.forEach((a) => {
        if (a.is_class_teacher && !seen.has(a.class_section_id)) {
          seen.add(a.class_section_id);
          list.push({ id: a.class_section_id, name: a.ClassSection?.name, section: a.ClassSection?.section });
        }
      });
      setCtClasses(list);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="My Class Students"
        subtitle="View, add, edit, or remove students in your class. Only class teachers have this access."
      />
      {ctClasses.length === 0 ? (
        <div className="td-card">
          <EmptyState icon="🔒" title="Class Teacher Access Required"
            sub="You are not assigned as class teacher for any class. Contact your school admin." />
        </div>
      ) : (
        ctClasses.map((cls) => <ClassStudentsCard key={cls.id} classInfo={cls} />)
      )}
    </div>
  );
}

function ClassStudentsCard({ classInfo }) {
  const [students, setStudents]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [sortBy, setSortBy]       = useState("roll");
  const [sortDir, setSortDir]     = useState("asc");
  const [search, setSearch]       = useState("");
  const [modal, setModal]         = useState(null); // null | { mode:"add"|"edit", student? }
  const [form, setForm]           = useState(BLANK_FORM);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const [msg, setMsg]             = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/teacher/class-students", { params: { classSectionId: classInfo.id } })
      .then((r) => setStudents(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [classInfo.id]);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setForm({ ...BLANK_FORM, class_section_id: classInfo.id });
    setModal({ mode: "add" });
    setMsg("");
  }

  function openEdit(s) {
    const f = {};
    STUDENT_FIELDS.forEach(({ key }) => { f[key] = s[key] ?? ""; });
    f.class_section_id = classInfo.id;
    setForm(f);
    setModal({ mode: "edit", student: s });
    setMsg("");
  }

  async function saveForm(e) {
    e.preventDefault();
    if (!form.name?.trim() || !form.roll_no?.toString().trim()) {
      setMsg("Name and Roll No are required."); return;
    }
    setSaving(true); setMsg("");
    try {
      if (modal.mode === "add") {
        await api.post("/teacher/class-students", { ...form, classSectionId: classInfo.id });
      } else {
        await api.put(`/teacher/class-students/${modal.student.id}`, form);
      }
      setModal(null);
      load();
    } catch (err) {
      setMsg(err.response?.data?.error || "Could not save student.");
    } finally { setSaving(false); }
  }

  async function handleDelete(s) {
    if (!window.confirm(`Remove ${s.name} from this class? This is a soft-delete and preserves their marks.`)) return;
    setDeleting(s.id);
    try {
      await api.delete(`/teacher/class-students/${s.id}`);
      setStudents((prev) => prev.filter((x) => x.id !== s.id));
    } catch (err) {
      alert(err.response?.data?.error || "Could not remove student.");
    } finally { setDeleting(null); }
  }

  const dir = sortDir === "asc" ? 1 : -1;
  const filtered = students
    .filter((s) => !search || s.name.toLowerCase().includes(search.toLowerCase()) || String(s.roll_no ?? "").includes(search))
    .sort((a, b) =>
      sortBy === "name"
        ? dir * a.name.localeCompare(b.name)
        : dir * ((parseInt(a.roll_no) || 0) - (parseInt(b.roll_no) || 0))
    );

  return (
    <div className="td-card">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#1E293B" }}>
            {classInfo.name}{classInfo.section ? ` — ${classInfo.section}` : ""}
          </div>
          <span className="td-pill td-pill-purple" style={{ marginTop: 6, display: "inline-flex" }}>⭐ Class Teacher</span>
        </div>
        <button
          className="btn"
          onClick={openAdd}
          style={{ background: "#16A34A", borderColor: "#16A34A", fontWeight: 700 }}
        >
          + Add Student
        </button>
      </div>

      {/* Search + Sort */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <input
          type="text" placeholder="Search name or roll no…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 160, fontSize: 13, padding: "7px 12px", borderRadius: 10, border: "1px solid #E2E8F0" }}
        />
        <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Sort:</span>
        {[{ val: "roll", label: "Roll No" }, { val: "name", label: "A–Z" }].map(({ val, label }) => {
          const active = sortBy === val;
          return (
            <button key={val}
              onClick={() => { if (sortBy === val) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortBy(val); setSortDir("asc"); } }}
              style={{
                padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
                background: active ? "#EDE9FE" : "#F1F5F9", color: active ? "#6D28D9" : "#64748B",
                border: `1px solid ${active ? "#C4B5FD" : "#E2E8F0"}`,
              }}
            >
              {label} {active ? (sortDir === "asc" ? "↑" : "↓") : ""}
            </button>
          );
        })}
      </div>

      {loading && <Spinner />}

      {!loading && students.length === 0 && (
        <EmptyState icon="👥" title="No students yet." sub="Click '+ Add Student' to add your first student." />
      )}

      {/* Table — desktop */}
      {!loading && filtered.length > 0 && (
        <>
          <div style={{ overflowX: "auto" }}>
            <table style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ width: 60, textAlign: "center" }}>Roll</th>
                  <th>Name</th>
                  <th style={{ width: 110 }}>Admission No</th>
                  <th style={{ width: 80 }}>Gender</th>
                  <th style={{ width: 110 }}>Mobile</th>
                  <th style={{ width: 120 }}>Father / Guardian</th>
                  <th style={{ width: 100, textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id}>
                    <td style={{ textAlign: "center", fontWeight: 700, color: "#6D28D9" }}>{s.roll_no ?? "—"}</td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{s.name}</div>
                      {s.dob && <div style={{ fontSize: 11, color: "#94A3B8" }}>DOB: {formatDob(s.dob)}</div>}
                    </td>
                    <td style={{ color: "#64748B" }}>{s.admission_no || "—"}</td>
                    <td style={{ color: "#64748B" }}>{s.gender || "—"}</td>
                    <td style={{ color: "#64748B" }}>{s.mobile || "—"}</td>
                    <td style={{ color: "#64748B" }}>{s.father_name || s.parent_name || "—"}</td>
                    <td style={{ textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        <button
                          className="btn sm secondary"
                          onClick={() => openEdit(s)}
                          style={{ fontSize: 11, padding: "3px 10px" }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          className="btn sm"
                          onClick={() => handleDelete(s)}
                          disabled={deleting === s.id}
                          style={{ fontSize: 11, padding: "3px 10px", background: "#DC2626", borderColor: "#DC2626" }}
                        >
                          {deleting === s.id ? "…" : "🗑"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: "#94A3B8", marginTop: 10 }}>
            {filtered.length} of {students.length} students shown
          </p>
        </>
      )}

      {/* Add / Edit Modal */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.2)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
                {modal.mode === "add" ? "Add New Student" : `Edit — ${modal.student.name}`}
              </h3>
              <button onClick={() => setModal(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748B" }}>✕</button>
            </div>

            <form onSubmit={saveForm}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {STUDENT_FIELDS.map(({ key, label, required, type, options }) => (
                  <div key={key} style={{ gridColumn: key === "name" ? "1 / -1" : undefined }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#64748B", display: "block", marginBottom: 4 }}>
                      {label}{required && <span style={{ color: "#DC2626" }}> *</span>}
                    </label>
                    {type === "select" ? (
                      <select
                        value={form[key] || ""}
                        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                        style={{ width: "100%", fontSize: 13 }}
                      >
                        {options.map((o) => <option key={o} value={o}>{o || "— Select —"}</option>)}
                      </select>
                    ) : (
                      <input
                        type={type || "text"}
                        value={form[key] || ""}
                        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                        required={required}
                        style={{ width: "100%", fontSize: 13 }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {msg && (
                <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "#DC2626", fontSize: 13 }}>
                  {msg}
                </div>
              )}

              <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button type="button" className="btn secondary" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn" disabled={saving}>
                  {saving ? "Saving…" : modal.mode === "add" ? "Add Student" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   Attendance (Class Teacher only)
   ========================================================= */
function Attendance() {
  const [classTeacherClasses, setClassTeacherClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/teacher/my-assignments").then((r) => {
      const seen = new Set(); const ctClasses = [];
      r.data.forEach((a) => {
        if (a.is_class_teacher && !seen.has(a.class_section_id)) {
          seen.add(a.class_section_id);
          ctClasses.push({ id: a.class_section_id, name: a.ClassSection?.name, section: a.ClassSection?.section });
        }
      });
      setClassTeacherClasses(ctClasses);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Attendance"
        subtitle="Enter total working days and days present for your class. Only class teachers can manage attendance."
      />

      {classTeacherClasses.length === 0 ? (
        <div className="td-card">
          <EmptyState
            icon="🔒"
            title="Class Teacher Access Required"
            sub="You are not assigned as class teacher for any class. Contact your school admin to get assigned."
          />
        </div>
      ) : (
        classTeacherClasses.map((cls) => (
          <ClassAttendanceCard key={cls.id} classInfo={cls} />
        ))
      )}
    </div>
  );
}

function ClassAttendanceCard({ classInfo }) {
  const [sessions, setSessions]       = useState([]);
  const [session, setSession]         = useState("");
  const [data, setData]               = useState(null);
  const [workingDays, setWorkingDays] = useState("");
  const [presentMap, setPresentMap]   = useState({});
  const [saving, setSaving]           = useState(false);
  const [msg, setMsg]                 = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [sortBy, setSortBy]           = useState("roll"); // "roll" | "name"
  const [sortDir, setSortDir]         = useState("asc");

  useEffect(() => {
    api.get("/teacher/my-exams").then((r) => {
      const sessSet = new Set(r.data.map((e) => e.session).filter(Boolean));
      const list = [...sessSet].sort().reverse();
      setSessions(list);
      if (list.length > 0) setSession(list[0]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!session) return;
    setDataLoading(true); setMsg("");
    api.get("/teacher/attendance/term", { params: { classSectionId: classInfo.id, session } })
      .then(({ data: d }) => {
        setData(d);
        setWorkingDays(d.workingDays > 0 ? String(d.workingDays) : "");
        const p = {};
        d.students.forEach((s) => { p[s.id] = s.daysPresent !== null ? String(s.daysPresent) : ""; });
        setPresentMap(p);
      })
      .catch(() => setData(null))
      .finally(() => setDataLoading(false));
  }, [classInfo.id, session]);

  async function save() {
    const wd = Number(workingDays);
    if (!wd || wd < 1) { setMsg("Please enter total working days (must be ≥ 1)."); return; }
    setSaving(true); setMsg("");
    try {
      const records = data.students.map((s) => ({
        studentId: s.id,
        daysPresent: presentMap[s.id] !== "" ? Number(presentMap[s.id]) : 0,
      }));
      await api.post("/teacher/attendance/term", { classSectionId: classInfo.id, session, workingDays: wd, records });
      setMsg("Saved successfully.");
    } catch (err) { setMsg("Error: " + (err.response?.data?.error || "Could not save.")); }
    finally { setSaving(false); }
  }

  const wd = Number(workingDays) || 0;

  return (
    <div className="td-card">
      {/* Header */}
      <div className="td-att-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 14 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#1E293B" }}>
            {classInfo.name}{classInfo.section && ` — ${classInfo.section}`}
          </div>
          <span className="td-pill td-pill-purple" style={{ marginTop: 6, display: "inline-flex" }}>⭐ Class Teacher</span>
        </div>
        <div className="form-field" style={{ margin: 0, minWidth: 180, width: "100%" }}>
          <label className="form-label">Session</label>
          <select value={session} onChange={(e) => setSession(e.target.value)}>
            <option value="">— Select session —</option>
            {sessions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Sort controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Sort:</span>
        {[{ val: "roll", label: "Roll No" }, { val: "name", label: "A–Z" }].map(({ val, label }) => {
          const active = sortBy === val;
          return (
            <button
              key={val}
              onClick={() => { if (sortBy === val) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortBy(val); setSortDir("asc"); } }}
              style={{
                padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
                background: active ? "#EDE9FE" : "#F1F5F9", color: active ? "#6D28D9" : "#64748B",
                border: `1px solid ${active ? "#C4B5FD" : "#E2E8F0"}`,
              }}
            >
              {label} {active ? (sortDir === "asc" ? "↑" : "↓") : ""}
            </button>
          );
        })}
      </div>

      {/* Working days */}
      <div style={{ background: "linear-gradient(135deg, #F0F9FF, #EFF6FF)", border: "1px solid #BAE6FD", borderRadius: 14, padding: "16px 20px", marginBottom: 20 }}>
        <div style={{ fontSize: 12, color: "#0369A1", fontWeight: 700, marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Total Working Days — applies to all students
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <input
            type="number" min="1" max="365"
            value={workingDays}
            onChange={(e) => setWorkingDays(e.target.value)}
            placeholder="e.g. 220"
            style={{ width: 120, fontSize: 26, fontWeight: 900, textAlign: "center", borderRadius: 12, padding: "10px 12px", border: "2px solid #BAE6FD", outline: "none" }}
          />
          {wd > 0 && data && (
            <button type="button" className="btn sm secondary"
              onClick={() => {
                const p = {};
                data.students.forEach((s) => { p[s.id] = String(wd); });
                setPresentMap(p);
              }}>
              Set all = {wd} days
            </button>
          )}
        </div>
      </div>

      {dataLoading && <Spinner />}

      {msg && <Alert type={msg.startsWith("Error") ? "error" : "success"}>{msg}</Alert>}

      {!dataLoading && data && session && (
        data.students.length === 0 ? (
          <EmptyState icon="👥" title="No students in this class." />
        ) : (() => {
          const dir = sortDir === "asc" ? 1 : -1;
          const sortedStudents = [...data.students].sort((a, b) =>
            sortBy === "name"
              ? dir * a.name.localeCompare(b.name)
              : dir * ((parseInt(a.rollNo) || 0) - (parseInt(b.rollNo) || 0))
          );
          return (
          <>
            {/* Desktop table */}
            <div className="td-att-desktop" style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 60, textAlign: "center" }}>Roll</th>
                    <th>Student</th>
                    <th style={{ width: 80, textAlign: "center" }}>Working Days</th>
                    <th style={{ width: 140 }}>Days Present</th>
                    <th style={{ width: 100, textAlign: "center" }}>Attendance %</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents.map((s) => {
                    const dp = Number(presentMap[s.id]) || 0;
                    const pct = wd > 0 ? Math.round((dp / wd) * 100) : null;
                    const pctColor = pct === null ? "#94A3B8" : pct >= 75 ? "#16A34A" : pct >= 60 ? "#D97706" : "#DC2626";
                    const overLimit = wd > 0 && Number(presentMap[s.id]) > wd;
                    return (
                      <tr key={s.id} style={{ background: overLimit ? "#FEF2F2" : "#fff" }}>
                        <td style={{ textAlign: "center", fontWeight: 700 }}>{s.rollNo}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{s.name}</div>
                          <div style={{ fontSize: 11, color: "#94A3B8" }}>{s.admissionNo}</div>
                        </td>
                        <td style={{ textAlign: "center", color: "#64748B", fontWeight: 600 }}>{wd || "—"}</td>
                        <td>
                          <input
                            type="number" min="0" max={wd || 365}
                            value={presentMap[s.id] ?? ""}
                            onChange={(e) => setPresentMap((p) => ({ ...p, [s.id]: e.target.value }))}
                            style={{ width: 90, textAlign: "center", fontWeight: 700, borderColor: overLimit ? "#DC2626" : undefined }}
                            placeholder="0"
                          />
                          {overLimit && <div style={{ fontSize: 11, color: "#DC2626", marginTop: 2 }}>exceeds limit</div>}
                        </td>
                        <td style={{ textAlign: "center", fontWeight: 900, fontSize: 15, color: pctColor }}>
                          {pct !== null ? `${pct}%` : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile student cards */}
            <div className="td-att-mobile" style={{ flexDirection: "column", gap: 10 }}>
              {sortedStudents.map((s) => {
                const dp = Number(presentMap[s.id]) || 0;
                const pct = wd > 0 ? Math.round((dp / wd) * 100) : null;
                const pctColor = pct === null ? "#94A3B8" : pct >= 75 ? "#16A34A" : pct >= 60 ? "#D97706" : "#DC2626";
                const overLimit = wd > 0 && Number(presentMap[s.id]) > wd;
                return (
                  <div key={s.id} style={{ background: overLimit ? "#FEF2F2" : "#F8FAFC", border: `1px solid ${overLimit ? "#FECACA" : "#E2E8F0"}`, borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 700, color: "#1E293B" }}>{s.name}</div>
                        <div style={{ fontSize: 12, color: "#94A3B8" }}>Roll: {s.rollNo} · {s.admissionNo}</div>
                      </div>
                      {pct !== null && (
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontWeight: 900, fontSize: 20, color: pctColor, lineHeight: 1 }}>{pct}%</div>
                          <div style={{ fontSize: 11, color: "#94A3B8" }}>attendance</div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 4 }}>
                        Days Present (of {wd || "?"} working days)
                      </label>
                      <input
                        type="number" min="0" max={wd || 365}
                        value={presentMap[s.id] ?? ""}
                        onChange={(e) => setPresentMap((p) => ({ ...p, [s.id]: e.target.value }))}
                        style={{ width: "100%", fontSize: 20, fontWeight: 800, textAlign: "center", padding: "10px", borderRadius: 10, borderColor: overLimit ? "#DC2626" : undefined }}
                        placeholder="0"
                      />
                      {overLimit && <div style={{ fontSize: 12, color: "#DC2626", marginTop: 4 }}>⚠ Exceeds total working days</div>}
                    </div>
                    {pct !== null && (
                      <div className="td-pct-bar-track" style={{ marginTop: 10 }}>
                        <div className="td-pct-bar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: pctColor }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 18, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <button className="btn" onClick={save} disabled={saving || !workingDays || !session} style={{ minWidth: 150 }}>
                {saving ? "Saving…" : "💾 Save Attendance"}
              </button>
              <span style={{ fontSize: 12, color: "#94A3B8" }}>{data.students.length} students · {session}</span>
            </div>
          </>
          );
        })()
      )}
    </div>
  );
}

/* =========================================================
   Remarks (class-wide, per exam)
   ========================================================= */
function Remarks() {
  const [classTeacherClasses, setClassTeacherClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/teacher/my-assignments").then((r) => {
      const seen = new Set(); const ctClasses = [];
      r.data.forEach((a) => {
        if (a.is_class_teacher && !seen.has(a.class_section_id)) {
          seen.add(a.class_section_id);
          ctClasses.push({ id: a.class_section_id, name: a.ClassSection?.name, section: a.ClassSection?.section });
        }
      });
      setClassTeacherClasses(ctClasses);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Student Remarks"
        subtitle="Write remarks for each student per exam. These appear on result cards. Only class teachers can add remarks."
      />

      {classTeacherClasses.length === 0 ? (
        <div className="td-card">
          <EmptyState
            icon="🔒"
            title="Class Teacher Access Required"
            sub="Only the designated class teacher can write student remarks. Contact your school admin."
          />
        </div>
      ) : (
        classTeacherClasses.map((cls) => (
          <ClassRemarksCard key={cls.id} classInfo={cls} />
        ))
      )}
    </div>
  );
}

function ClassRemarksCard({ classInfo }) {
  const [exams, setExams]       = useState([]);
  const [examId, setExamId]     = useState("");
  const [students, setStudents] = useState([]);
  const [remarks, setRemarks]   = useState({});
  const [saving, setSaving]     = useState(null);
  const [saved, setSaved]       = useState({});
  const [loading, setLoading]   = useState(false);
  const [sortBy, setSortBy]     = useState("roll"); // "roll" | "name"
  const [sortDir, setSortDir]   = useState("asc");

  useEffect(() => {
    api.get("/teacher/my-exams").then((r) => {
      setExams(r.data);
      if (r.data.length > 0) setExamId(String(r.data[0].id));
    }).catch(() => {});
  }, []);

  const loadRemarks = useCallback(async () => {
    if (!examId) return;
    setLoading(true); setSaved({});
    try {
      const { data } = await api.get("/teacher/remarks/class", { params: { classSectionId: classInfo.id, examId } });
      setStudents(data);
      const map = {};
      data.forEach((s) => { map[s.id] = s.remark || ""; });
      setRemarks(map);
    } catch (_) { setStudents([]); }
    finally { setLoading(false); }
  }, [classInfo.id, examId]);

  useEffect(() => { loadRemarks(); }, [loadRemarks]);

  async function saveRemark(studentId) {
    setSaving(studentId); setSaved((prev) => ({ ...prev, [studentId]: false }));
    try {
      await api.post("/teacher/remarks/class", { studentId, examId: Number(examId), remark: remarks[studentId] || "" });
      setSaved((prev) => ({ ...prev, [studentId]: true }));
      setTimeout(() => setSaved((prev) => ({ ...prev, [studentId]: false })), 2500);
    } catch (err) {
      alert(err.response?.data?.error || "Could not save remark.");
    } finally { setSaving(null); }
  }

  async function saveAll() {
    for (const s of students) await saveRemark(s.id);
  }

  return (
    <div className="td-card">
      {/* Header */}
      <div className="td-remarks-header" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 14 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#1E293B" }}>
            {classInfo.name}{classInfo.section && ` — ${classInfo.section}`}
          </div>
          <span className="td-pill td-pill-purple" style={{ marginTop: 6, display: "inline-flex" }}>⭐ Class Teacher</span>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="form-field" style={{ margin: 0, minWidth: 200 }}>
            <label className="form-label">Exam</label>
            <select value={examId} onChange={(e) => setExamId(e.target.value)}>
              <option value="">— Select exam —</option>
              {exams.map((e) => (
                <option key={e.id} value={e.id}>{e.name} ({e.session})</option>
              ))}
            </select>
          </div>
          {students.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>Sort:</span>
                {[{ val: "roll", label: "Roll No" }, { val: "name", label: "A–Z" }].map(({ val, label }) => {
                  const active = sortBy === val;
                  return (
                    <button
                      key={val}
                      onClick={() => { if (sortBy === val) setSortDir((d) => d === "asc" ? "desc" : "asc"); else { setSortBy(val); setSortDir("asc"); } }}
                      style={{
                        padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
                        background: active ? "#EDE9FE" : "#F1F5F9", color: active ? "#6D28D9" : "#64748B",
                        border: `1px solid ${active ? "#C4B5FD" : "#E2E8F0"}`,
                      }}
                    >
                      {label} {active ? (sortDir === "asc" ? "↑" : "↓") : ""}
                    </button>
                  );
                })}
              </div>
              <button className="btn sm" onClick={saveAll} disabled={saving !== null}>Save All</button>
            </>
          )}
        </div>
      </div>

      {loading && <Spinner />}

      {!loading && examId && students.length === 0 && (
        <EmptyState icon="📝" title="No students in this class." />
      )}

      {!loading && students.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {[...students].sort((a, b) => {
            const dir = sortDir === "asc" ? 1 : -1;
            return sortBy === "name"
              ? dir * a.name.localeCompare(b.name)
              : dir * ((parseInt(a.rollNo) || 0) - (parseInt(b.rollNo) || 0));
          }).map((s) => (
            <div key={s.id} className="td-remarks-row" style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "14px 0", borderBottom: "1px solid #F1F5F9" }}>
              <div style={{ minWidth: 130, flexShrink: 0, paddingTop: 4 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1E293B" }}>{s.name}</div>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>Roll: {s.rollNo}</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <textarea
                  rows={2}
                  value={remarks[s.id] || ""}
                  onChange={(e) => setRemarks((prev) => ({ ...prev, [s.id]: e.target.value }))}
                  placeholder="Write remark for this student (appears on result card)…"
                  style={{
                    width: "100%", fontSize: 13, resize: "vertical", borderRadius: 10,
                    borderColor: saved[s.id] ? "#6EE7B7" : undefined,
                    background: saved[s.id] ? "#F0FDF4" : undefined,
                    transition: "background .3s, border-color .3s",
                  }}
                />
              </div>
              <div className="td-remarks-save-col" style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, paddingTop: 4 }}>
                <button
                  className="btn sm"
                  onClick={() => saveRemark(s.id)}
                  disabled={saving === s.id}
                  style={{ minWidth: 64 }}
                >
                  {saving === s.id ? "…" : "Save"}
                </button>
                {saved[s.id] && <span style={{ fontSize: 11, color: "#16A34A", fontWeight: 600 }}>✓ Saved</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   My Profile & Settings
   ========================================================= */
function MyProfile() {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const [saving, setSaving] = useState(false);

  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });

  useEffect(() => { loadProfile(); }, []);

  async function loadProfile() {
    try {
      const { data } = await api.get("/teacher/profile");
      setProfile(data);
      setForm({
        name: data.name || "", email: data.email || "", mobile: data.mobile || "",
        designation: data.designation || "", qualification: data.qualification || "", address: data.address || "",
      });
    } catch (_) {}
  }

  async function saveProfile(e) {
    e.preventDefault(); setSaveMsg(""); setSaveErr(""); setSaving(true);
    try {
      const { data } = await api.put("/teacher/profile", form);
      setProfile((prev) => ({ ...prev, ...data }));
      setSaveMsg("Profile updated successfully.");
      setEditMode(false);
    } catch (err) { setSaveErr(err.response?.data?.error || "Failed to update profile."); }
    finally { setSaving(false); }
  }

  async function changePassword(e) {
    e.preventDefault(); setPwMsg(""); setPwErr("");
    if (pwForm.newPassword !== pwForm.confirmPassword) { setPwErr("New passwords do not match."); return; }
    if (pwForm.newPassword.length < 6) { setPwErr("New password must be at least 6 characters."); return; }
    setPwSaving(true);
    try {
      await api.post("/teacher/change-password", { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwMsg("Password changed successfully!");
      setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) { setPwErr(err.response?.data?.error || "Failed to change password."); }
    finally { setPwSaving(false); }
  }

  if (!profile) return <Spinner />;

  const PwToggle = ({ field }) => (
    <button type="button" onClick={() => setShowPw((p) => ({ ...p, [field]: !p[field] }))}
      style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14, padding: 0 }}>
      {showPw[field] ? "🙈" : "👁"}
    </button>
  );

  const initials = profile.name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";

  return (
    <div>
      <PageHeader title="My Profile" subtitle="Manage your personal information and account security." />

      {profile.must_reset_password && (
        <Alert type="warn">
          <strong>Action Required:</strong> You are using the temporary password assigned by your admin. Please change it below.
        </Alert>
      )}

      {/* Avatar + info banner */}
      <div className="td-card" style={{ display: "flex", alignItems: "center", gap: 18, background: "linear-gradient(135deg, #F8FAFC, #F1F5F9)", flexWrap: "wrap" }}>
        <div style={{
          width: 64, height: 64, borderRadius: 18, flexShrink: 0,
          background: "linear-gradient(135deg, #1E3A5F, #2D5082)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, fontWeight: 800, color: "#fff",
        }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: "#1E293B" }}>{profile.name}</div>
          {profile.designation && <div style={{ fontSize: 13, color: "#64748B" }}>{profile.designation}</div>}
          {profile.School && <div style={{ fontSize: 13, color: "#64748B" }}>{profile.School.name}</div>}
        </div>
        <div style={{ background: "#F1F5F9", borderRadius: 10, padding: "8px 14px" }}>
          <div style={{ fontSize: 11, color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Login ID</div>
          <code style={{ fontSize: 14, fontWeight: 800, color: "#1E293B" }}>{profile.username}</code>
        </div>
      </div>

      {/* Personal info */}
      <div className="td-card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#1E293B" }}>Personal Information</div>
          {!editMode && (
            <button className="btn secondary" onClick={() => { setEditMode(true); setSaveMsg(""); setSaveErr(""); }}>
              Edit Profile
            </button>
          )}
        </div>

        {saveMsg && <Alert type="success">{saveMsg}</Alert>}
        {saveErr && <Alert type="error">{saveErr}</Alert>}

        {editMode ? (
          <form onSubmit={saveProfile}>
            <div className="td-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div className="form-field" style={{ margin: 0, gridColumn: "1 / -1" }}>
                <label className="form-label">Full Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label className="form-label">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" />
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label className="form-label">Mobile</label>
                <input type="tel" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="10-digit number" />
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label className="form-label">Designation</label>
                <input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g. Senior Teacher" />
              </div>
              <div className="form-field" style={{ margin: 0 }}>
                <label className="form-label">Qualification</label>
                <input value={form.qualification} onChange={(e) => setForm({ ...form, qualification: e.target.value })} placeholder="e.g. B.Ed, M.Sc" />
              </div>
              <div className="form-field" style={{ margin: 0, gridColumn: "1 / -1" }}>
                <label className="form-label">Address</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Residential address" />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
              <button className="btn" type="submit" disabled={saving}>{saving ? "Saving…" : "Save Changes"}</button>
              <button className="btn secondary" type="button" onClick={() => setEditMode(false)}>Cancel</button>
            </div>
          </form>
        ) : (
          <div className="td-profile-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16 }}>
            {[
              { label: "Full Name", value: profile.name },
              { label: "Email", value: profile.email || "—" },
              { label: "Mobile", value: profile.mobile || "—" },
              { label: "Designation", value: profile.designation || "—" },
              { label: "Qualification", value: profile.qualification || "—" },
              { label: "Address", value: profile.address || "—", full: true },
            ].map(({ label, value, full }) => (
              <div key={label} style={full ? { gridColumn: "1 / -1" } : {}}>
                <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 3, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
                <div style={{ fontWeight: 500, color: "#1E293B", fontSize: 14 }}>{value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Change Password */}
      <div className="td-card">
        <div style={{ fontWeight: 800, fontSize: 16, color: "#1E293B", marginBottom: 18 }}>Change Password</div>

        {pwMsg && <Alert type="success">{pwMsg}</Alert>}
        {pwErr && <Alert type="error">{pwErr}</Alert>}

        <form onSubmit={changePassword}>
          <div className="td-pw-wrap" style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 420 }}>
            {[
              { key: "current", label: "Current Password", field: "currentPassword" },
              { key: "new", label: "New Password", field: "newPassword", hint: "Minimum 6 characters" },
              { key: "confirm", label: "Confirm New Password", field: "confirmPassword" },
            ].map(({ key, label, field, hint }) => (
              <div className="form-field" key={field} style={{ margin: 0 }}>
                <label className="form-label">{label}</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPw[key] ? "text" : "password"}
                    value={pwForm[field]}
                    onChange={(e) => setPwForm({ ...pwForm, [field]: e.target.value })}
                    required
                    style={{ paddingRight: 36 }}
                    placeholder={hint || ""}
                  />
                  <PwToggle field={key} />
                </div>
                {hint && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 3 }}>{hint}</div>}
              </div>
            ))}
            <button className="btn" type="submit" disabled={pwSaving} style={{ alignSelf: "flex-start" }}>
              {pwSaving ? "Changing…" : "Change Password"}
            </button>
          </div>
        </form>
      </div>

      {/* Sign Out */}
      <div className="td-card" style={{ border: "1px solid #FECACA" }}>
        <div style={{ fontWeight: 800, fontSize: 16, color: "#991B1B", marginBottom: 8 }}>Sign Out</div>
        <p style={{ fontSize: 14, color: "#64748B", marginBottom: 16 }}>
          You will be logged out and redirected to the login page.
        </p>
        <button className="btn danger" onClick={logout}>Sign Out</button>
      </div>
    </div>
  );
}
